import { Suspense, lazy, useEffect, useState } from 'react';
import NatalWheelSkeleton from '@/components/NatalWheelSkeleton';

// Тяжёлые части натальной карты (колесо, вкладки и библиотека трактовок)
// живут в отдельном куске сборки: первый экран больше не ждёт их разбора.
const NatalChartWheelLazy = lazy(() => import('@/components/natal/NatalChartWheel'));
const HousesTabLazy = lazy(() => import('@/components/natal/ChartTabs').then((m) => ({ default: m.HousesTab })));
const PlanetsTabLazy = lazy(() => import('@/components/natal/ChartTabs').then((m) => ({ default: m.PlanetsTab })));
const AspectsTabLazy = lazy(() => import('@/components/natal/ChartTabs').then((m) => ({ default: m.AspectsTab })));
// Полоса расшифровок вошедшего. Тянет ChartTabs и корпус трактовок, поэтому
// живёт здесь, рядом с остальным тяжёлым: гостю она не рендерится и не
// скачивается — у него полоса питается сервером (NatalPreviewBlocks).
const NatalFullStripLazy = lazy(() => import('@/components/natal/NatalFullStrip'));

const onLanding =
  typeof window !== 'undefined' && (window.location.pathname === '/' || window.location.pathname === '');

// Есть ли у посетителя сохранённая карта. Если есть, он вернулся смотреть свою:
// живое колесо и тяжёлый кусок нужны сразу, поведение для него не меняется.
export const hasSavedChart = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(
      window.localStorage.getItem('birthData') || window.localStorage.getItem('astrofeel_natal_form')
    );
  } catch (e) {
    return false;
  }
})();

let preloadStarted = false;
export function preloadChart() {
  if (preloadStarted) return;
  preloadStarted = true;
  import('@/components/natal/NatalChartWheel');
  import('@/components/natal/ChartTabs');
}

// Гость видит статичную картинку колеса, пока не тронул страницу.
let touched = !onLanding || hasSavedChart;
const touchSubs = new Set();
function markTouched() {
  if (touched) return;
  touched = true;
  touchSubs.forEach((fn) => fn());
  preloadChart();
}

if (onLanding) {
  if (hasSavedChart) {
    preloadChart();
  } else {
    const events = ['pointerdown', 'keydown', 'touchstart'];
    const onFirstTouch = () => {
      events.forEach((name) => window.removeEventListener(name, onFirstTouch));
      markTouched();
    };
    events.forEach((name) => window.addEventListener(name, onFirstTouch, { passive: true }));
  }
}

function useTouched() {
  const [value, setValue] = useState(touched);
  useEffect(() => {
    if (value) return undefined;
    const fn = () => setValue(true);
    touchSubs.add(fn);
    return () => {
      touchSubs.delete(fn);
    };
  }, [value]);
  return value;
}

// Статичная картинка колеса: та же демо-карта, что рисует живой компонент.
// Вставляем разметкой, а не картинкой, чтобы подтянулись иконки знаков и планет.
// Разметка колеса уже лежит в index.html (статичный первый экран). Забираем её оттуда,
// пока React не затёр заглушку, чтобы не качать тот же файл второй раз.
const BOOT_WHEEL = (() => {
  if (typeof document === 'undefined') return '';
  try {
    const el = document.querySelector('#boot .max-w-\\[460px\\] > div');
    return el ? el.innerHTML : '';
  } catch (e) {
    return '';
  }
})();

export function WheelPreview() {
  const [markup, setMarkup] = useState(BOOT_WHEEL);
  useEffect(() => {
    if (markup) return undefined;
    let alive = true;
    fetch('/img/wheel-preview.svg')
      .then((r) => (r.ok ? r.text() : ''))
      .then((t) => {
        if (alive) setMarkup(t);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [markup]);
  if (!markup) return <NatalWheelSkeleton />;
  // Мышь над картинкой — будим живое колесо: иначе гость на ПК водит по
  // линиям аспектов, а подсказки нет, пока он где-нибудь не кликнет.
  // Слушаем только сам круг, не всю страницу, — тяжёлый кусок по-прежнему
  // не едет тем, кто колеса не касался.
  return (
    <div
      aria-hidden="true"
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') markTouched(); }}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}

export default function NatalChartWheel(props) {
  const ready = useTouched();
  if (!ready) return <WheelPreview />;
  return (
    <Suspense fallback={hasSavedChart ? <NatalWheelSkeleton /> : <WheelPreview />}>
      <NatalChartWheelLazy {...props} />
    </Suspense>
  );
}

export function HousesTab(props) {
  return (
    <Suspense fallback={null}>
      <HousesTabLazy {...props} />
    </Suspense>
  );
}

export function PlanetsTab(props) {
  return (
    <Suspense fallback={null}>
      <PlanetsTabLazy {...props} />
    </Suspense>
  );
}

export function AspectsTab(props) {
  return (
    <Suspense fallback={null}>
      <AspectsTabLazy {...props} />
    </Suspense>
  );
}

export function NatalFullStrip(props) {
  return (
    <Suspense fallback={null}>
      <NatalFullStripLazy {...props} />
    </Suspense>
  );
}
